import React, { useState } from "react";
import {
  useParams, Link as RouterLink, useLocation, Routes, Route,
} from "react-router-dom";
import {
  Box, Typography, Grid, Button, Chip, Tabs, Tab,
} from "@mui/material";
import {
  CheckCircle, RadioButtonUnchecked, Warning, Hub,
  Schedule, Add, Delete,
} from "@mui/icons-material";
import { formatDate, getCurrentPathTab } from "../services/Utils";
import BreadCrumbsRendered from "../components/BreadCrumbsRendered";
import SdvFeatureVehicleMap from "../components/sdv-feature/SdvFeatureVehicleMap";
import SdvVehicleFeatureDataGrid from "../components/sdv-feature/SdvFeatureVehicleDataGrid";
import { useQuery } from "react-query";
import { useToast } from "../components/toast";
import { getSdvFeatureById } from "../services/SdvFeature";
import DeleteVehicleFeatureDialog from "../components/sdv-feature/DeleteVehicleFeatureDialog";
import Constants from "../services/Constants";

const PATHS = ["overview", "vehicles"];

const UploadStatusChip = ({ status, UploadStatus }) => {
  if (status === UploadStatus.Completed)
    return (
      <Typography sx={{ fontSize: "12px", color: "#3B6D11", display: "flex", alignItems: "center", gap: 0.5 }}>
        <CheckCircle sx={{ fontSize: 13 }} /> Completed
      </Typography>
    );
  if (status === UploadStatus.Failed)
    return (
      <Typography sx={{ fontSize: "12px", color: "#A32D2D", display: "flex", alignItems: "center", gap: 0.5 }}>
        <Warning sx={{ fontSize: 13 }} /> Failed
      </Typography>
    );
  if (status === UploadStatus.InProgress)
    return (
      <Typography sx={{ fontSize: "12px", color: "#854F0B", display: "flex", alignItems: "center", gap: 0.5 }}>
        <Schedule sx={{ fontSize: 13 }} /> In progress
      </Typography>
    );
  return (
    <Typography sx={{ fontSize: "12px", color: "text.disabled", display: "flex", alignItems: "center", gap: 0.5 }}>
      <RadioButtonUnchecked sx={{ fontSize: 13 }} /> Pending
    </Typography>
  );
};

const SdvFeature = () => {
  const params = useParams();
  const { addToast } = useToast();
  const { pathname } = useLocation();
  const [value, setValue] = useState(getCurrentPathTab(pathname, PATHS, PATHS[0]));
  const [sdvDetails, setSdvDetails] = useState({});
  const [openModalSdv, setOpenModalSdv] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { UPLOAD_STATUS: UploadStatus } = Constants;

  useQuery(["getSdvFeature", params?.sdvFeatureId], getSdvFeatureById, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => setSdvDetails(data),
    onError: ({ response: { data: errorData } }) => {
      if (errorData) addToast({ type: "error", message: errorData.message, autoClose: 3000 });
    },
  });

  const img_status = sdvDetails?.image_uploadStatus === UploadStatus.Completed;
  const video_status = sdvDetails?.video_uploadStatus === UploadStatus.Completed;

  const UPLOAD_ITEMS = [
    { label: "APK file", status: sdvDetails?.apk_uploadStatus, iconBg: "#EAF3DE", iconColor: "#3B6D11" },
    { label: "Image", status: sdvDetails?.image_uploadStatus, iconBg: "#FAEEDA", iconColor: "#854F0B" },
    { label: "Preview video", status: sdvDetails?.video_uploadStatus, iconBg: "#FBEAF0", iconColor: "#993556" },
    { label: "Metadata", status: sdvDetails?.metadata_uploadStatus, iconBg: "#E6F1FB", iconColor: "#185FA5" },
  ];

  const STAT_FIELDS = [
    { label: "Vehicles mapped", value: sdvDetails?.vehicleCount ?? "—" },
    {
      label: "Status",
      value: sdvDetails?.isActive
        ? <Chip label="Active" size="small" sx={{ bgcolor: "#EAF3DE", color: "#3B6D11", fontWeight: 500, fontSize: "12px" }} />
        : <Chip label="Inactive" size="small" sx={{ bgcolor: "#F1EFE8", color: "#5F5E5A", fontWeight: 500, fontSize: "12px" }} />,
    },
    { label: "Uploaded by", value: sdvDetails?.uploadedBy ?? "—" },
    { label: "Last updated", value: formatDate(sdvDetails?.updatedAt) ?? "—" },
  ];

  const Overview = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5, mb: 3 }}>
        {STAT_FIELDS.map(({ label, value }) => (
          <Box key={label} sx={{ bgcolor: "background.default", borderRadius: 2, p: "14px 16px" }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.disabled", mb: 0.75 }}>
              {label}
            </Typography>
            <Box sx={{ fontSize: "22px", fontWeight: 500, color: "text.primary" }}>{value}</Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "0.5px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>Feature details</Typography>
          </Box>
          {[
            ["Feature name", sdvDetails?.sdvFeatureName],
            ["Description", sdvDetails?.description],
            ["Uploaded by", sdvDetails?.uploadedBy],
            ["Created at", formatDate(sdvDetails?.createdAt)],
            ["Updated at", formatDate(sdvDetails?.updatedAt)],
          ].map(([key, val]) => (
            <Box key={key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.25, borderBottom: "0.5px solid", borderColor: "divider", "&:last-child": { borderBottom: "none" } }}>
              <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>{key}</Typography>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "text.primary", maxWidth: "55%", textAlign: "right" }}>
                {val ?? "—"}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "0.5px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>Upload status</Typography>
          </Box>
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
            {UPLOAD_ITEMS.map(({ label, status, iconBg, iconColor }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.25, p: "10px 14px", border: "0.5px solid", borderColor: "divider", borderRadius: 1.5 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: iconBg, color: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 16 }}>
                  {label === "APK file" && "🤖"}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "text.primary" }}>{label}</Typography>
                  <UploadStatusChip status={status} UploadStatus={UploadStatus} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const Vehicles = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={() => setOpenModalSdv(true)}
          sx={{ borderRadius: 1.5, textTransform: "none", fontSize: "13px", borderColor: "divider", color: "text.primary" }}
        >
          Map vehicle
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Delete />}
          disabled={!selectedRows?.length}
          onClick={() => setOpenDeleteDialog(true)}
          sx={{ borderRadius: 1.5, textTransform: "none", fontSize: "13px", borderColor: "#F09595", color: "#A32D2D", bgcolor: "#FCEBEB", "&:hover": { bgcolor: "#F7C1C1" }, "&.Mui-disabled": { borderColor: "divider", color: "text.disabled", bgcolor: "transparent" } }}
        >
          Remove vehicle
        </Button>
      </Box>

      {openDeleteDialog && (
        <DeleteVehicleFeatureDialog
          id={sdvDetails?.sdvFeatureId}
          name={sdvDetails?.sdvFeatureName}
          selectedRows={selectedRows}
          toggleDialog={() => setOpenDeleteDialog(false)}
        />
      )}
      {openModalSdv && (
        <SdvFeatureVehicleMap
          open={openModalSdv}
          toggleDialog={() => setOpenModalSdv(false)}
          sdvData={sdvDetails}
        />
      )}
      <SdvVehicleFeatureDataGrid
        sdvFeatureId={sdvDetails?.sdvFeatureId}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
      />
    </Box>
  );

  return (
    <div className="fragmentContainer">
      <BreadCrumbsRendered pathname={pathname} />

      <Box sx={{ mt: 2, border: "0.5px solid", borderColor: "divider", borderRadius: 3, bgcolor: "background.paper", overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 1.5, bgcolor: "#E6F1FB", color: "#185FA5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Hub />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "text.primary" }}>
                {sdvDetails?.sdvFeatureName ?? "—"}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "text.secondary", mt: 0.25 }}>
                {sdvDetails?.isActive
                  ? <Chip label="Active" size="small" sx={{ bgcolor: "#EAF3DE", color: "#3B6D11", fontWeight: 500, fontSize: "11px", height: 20 }} />
                  : <Chip label="Inactive" size="small" sx={{ bgcolor: "#F1EFE8", color: "#5F5E5A", fontWeight: 500, fontSize: "11px", height: 20 }} />
                }
                &nbsp;· ID: {sdvDetails?.sdvFeatureId ?? "—"}
              </Typography>
            </Box>
          </Box>

          <Tabs
            value={value}
            onChange={(_, v) => setValue(v)}
            sx={{ "& .MuiTab-root": { fontSize: "13px", fontWeight: 500, textTransform: "none", minHeight: 44 }, "& .MuiTabs-indicator": { bgcolor: "#1a3c8f" } }}
          >
            <Tab label="Overview" value="overview" component={RouterLink} to="overview" />
            {img_status && video_status && (
              <Tab label="Vehicles" value="vehicles" component={RouterLink} to="vehicles" />
            )}
          </Tabs>
        </Box>

        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="overview" element={<Overview />} />
          {img_status && video_status && (
            <Route path="vehicles" element={<Vehicles />} />
          )}
        </Routes>
      </Box>
    </div>
  );
};

export default SdvFeature;